/**
 * Канонический корпус кабинета: договор адаптера, привязанного к МАРШРУТУ,
 * общая память корпуса и раскладка наборов прототипа по именам файлов.
 *
 * Зачем отдельный договор рядом с `DatasetAdapter` из `adapter.ts`. Тот
 * адаптер берётся за набор ПО ИМЕНИ, и это верно, пока имя принадлежит одному
 * домену (`farm-obligation-<id>.json`). Кабинет хозяйства читает имена ОБЩИЕ —
 * `meta.json`, `cycles.json`, `works-<цикл>.json`, — те же, что читают экраны
 * района, но со своим смыслом: «мои работы», а не «работы района». Выбирать
 * между ними по имени нечем, поэтому адаптер кабинета объявляет маршруты, на
 * которых он отвечает, и вне их молчит. Иначе первый же подключённый домен
 * начал бы отвечать на чужих канонических экранах.
 *
 * Память корпуса. Кабинет читает восемь наборов подряд, и каждый из них —
 * отдельный вызов `read()`. Без общей памяти одно открытие экрана стоило бы
 * восьми обходов сервера. Поэтому корпус живёт обещанием, ключ которого —
 * срез запроса; запись его забывает, и следующий экран читает уже новые числа.
 */

import { api, type QueryParams } from '../../../api/client';
import { VersionedMemo } from '../../../api/dataEpoch';
import { onPrincipalChange } from '../../../api/session';
import { FILES } from '../../data/loader';
import { cycleIdOf, loadCanonCorpus, type CanonCorpus, type CorpusBasis } from './canon';

export type { CorpusBasis };

/**
 * Имя цикла «все циклы»: кабинет хозяйства на каноне читает свои работы
 * целиком. Ключи циклов канона (`COTTON-2026`) не совпадают с зашитыми парами
 * прототипа (`cycle-cotton-2026`), и чтение по ним давало пустой кабинет при
 * 78 работах на сервере. Звёздочки в ключе цикла канона не бывает.
 */
export const ALL_CYCLES = '*';

/** Канонический адаптер одного домена экранов, привязанный к маршрутам. */
export interface RouteAdapter {
  /** Шаблоны маршрутов (`routes.ts`), которые этот адаптер кормит. */
  readonly routes: readonly string[];
  /**
   * Прочитать набор. `null` — «набор не мой»: индекс спросит следующий
   * адаптер, а если не ответит никто, источник откажет по имени набора.
   */
  read(name: string, params: QueryParams): Promise<unknown> | null;
}

/* ------------------------------------------------------------ память корпуса */

const inFlight = new VersionedMemo<CanonCorpus>();

/**
 * Поколение корпуса. Запись его двигает, провайдер источника на него подписан
 * — и все экраны перечитывают набор сами. Без счётчика отчёт, внесённый
 * сейчас, появился бы только после перезагрузки вкладки, и человек не знал бы,
 * дошла его запись или нет.
 */
let generation = 0;
const listeners = new Set<() => void>();

export function corpusGeneration(): number {
  return generation;
}

export function subscribeCorpus(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
}

function keyOf(params: QueryParams): string {
  return Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([name, value]) => `${name}=${String(value)}`)
    .sort()
    .join('&');
}

/**
 * Корпус для этого среза. Повторный вызов в том же срезе не стоит запроса.
 *
 * `basis` — чем набран список работ: `works` — «мои работы» (`/my/works`,
 * кабинет хозяйства и ввод за хозяйство), `queue` — очередь проверки района
 * (`/verification/queue`). Районной роли `/my/works` отвечает `no_scope`, и
 * очередь на нём была всегда пустой.
 */
export function corpus(params: QueryParams, basis: CorpusBasis = 'works'): Promise<CanonCorpus> {
  const key = `${basis}|${keyOf(params)}`;
  // Под версией данных (agroai-0e8.26, журнал №1638): на следующей навигации
  // корпус отдаётся, только если сервер снова подтвердил ту же версию; любая
  // запись (своя или чужая) её меняет. Неудача не запоминается: иначе один
  // сбой сети запер бы экран до перезагрузки вкладки.
  return inFlight.get(key, () => loadCanonCorpus(params, basis));
}

/**
 * Забыть корпус. Зовётся ПОСЛЕ записи: отчёт, внесённый сейчас, обязан быть
 * виден на следующем чтении, а не после перезагрузки страницы.
 */
export function forgetCorpus(): void {
  inFlight.clear();
  generation += 1;
  for (const listener of listeners) listener();
}

// Корпус срезан областью чтения вошедшего: сменился аккаунт — память не его.
onPrincipalChange(() => inFlight.clear());


/* --------------------------------------------------------- раскладка наборов */

/** Имя набора с идентификатором цикла: `works-<cycle>.json`. */
function suffix(name: string, prefix: string): string | null {
  if (!name.startsWith(prefix) || !name.endsWith('.json')) return null;
  return name.slice(prefix.length, -'.json'.length);
}

/**
 * Наборы прототипа, которые корпус кабинета умеет отдать сегодня.
 *
 * Чего в списке нет — того канон кабинету не отдаёт вовсе: карантин, техника,
 * вода, события цикла и внеплановые работы. Отказ по имени набора честнее
 * пустого массива: пустой массив экран показал бы как «ничего нет».
 */
export function readCanonDataset(
  name: string,
  params: QueryParams,
  basis: CorpusBasis = 'works',
): Promise<unknown> | null {
  if (basis === 'queue') return readQueueDataset(name, params);
  return pick(name, () => corpus(params, basis));
}

/* ------------------------------------------------------- очередь по циклам */

/** Набор очереди: циклы, где есть что проверять (решение №593). */
export const QUEUE_CYCLES = 'queue-cycles.json';

const CYCLE_SCOPE = '@';

/**
 * Имя набора очереди, суженного до цикла: `farms.json@<цикл>`. Хозяйства и
 * контуры очереди собираются из её строк, и для цикла это ДРУГИЕ строки, чем у
 * страницы района: имя без цикла показало бы на строках цикла голые номера.
 */
export function queueScoped(name: string, cycleId: string): string {
  return `${name}${CYCLE_SCOPE}${cycleId}`;
}

/**
 * Корпус очереди одного цикла: ось цикла уходит на сервер (`cycle=`), и
 * страница «по вниманию» считается внутри цикла. Цикл без ключа канона
 * («вне цикла») читается страницей района без оси.
 *
 * Ключ цикла берётся из списка циклов района (`/season/cycles` — тот же
 * запрос, что у оглавления, и в полёте он один), а НЕ из оглавления очереди:
 * оглавление ждёт пробу очереди по каждому циклу, и страница цикла с
 * раскрытием до контуров шла третьей волной после них — после суждения экран
 * ждал лишние секунды (agroai-b48).
 */
async function queueCycleCorpus(params: QueryParams, cycleId: string): Promise<CanonCorpus> {
  let key = cycleKeys.get(cycleId);
  if (key === undefined) {
    const seasons = await api.seasonCycles(params).catch(() => null);
    for (const row of seasons?.items ?? []) cycleKeys.set(cycleIdOf(row.season_label, row.crop_code), row.cycle);
    key = cycleKeys.get(cycleId);
  }
  return key ? corpus({ ...params, cycle: key }, 'queue') : corpus(params, 'queue');
}

/**
 * «Идентификатор цикла экрана → ключ цикла канона». Это не число и не срез, а
 * имя: пара «метка сезона × культура» в двух написаниях, и запись данных его
 * не меняет. Поэтому оно переживает эпоху данных — после суждения страница
 * цикла спрашивается сразу, а не после повторного `/season/cycles`
 * (agroai-b48). Сброс — со сменой участника, как у всей памяти корпуса.
 */
const cycleKeys = new Map<string, string>();
onPrincipalChange(() => cycleKeys.clear());

function readQueueDataset(name: string, params: QueryParams): Promise<unknown> | null {
  const index = () => corpus(params, 'queue-index');
  if (name === QUEUE_CYCLES) return index().then((data) => data.queuedCycles ?? []);
  const at = name.indexOf(CYCLE_SCOPE);
  if (at > 0) {
    const cycleId = name.slice(at + CYCLE_SCOPE.length);
    return pick(name.slice(0, at), () => queueCycleCorpus(params, cycleId));
  }
  // Оглавление: срез, циклы и культуры — без раскрытия работ.
  if (name === FILES.meta || name === FILES.cycles || name === FILES.crops) {
    return pick(name, index);
  }
  const worksOf = suffix(name, 'works-') ?? suffix(name, 'obligations-');
  if (worksOf !== null && worksOf !== ALL_CYCLES) {
    return pick(name, () => queueCycleCorpus(params, worksOf));
  }
  return pick(name, () => corpus(params, 'queue'));
}

/* -------------------------------------------- ввод за хозяйство по хозяйству */

const FARM_SCOPE = '@farm=';

/**
 * Имя набора ввода за хозяйство, суженного до ОДНОГО хозяйства:
 * `works-<цикл>.json@farm=<id>`. Районной роли «мои работы» без хозяйства
 * пусты (`/my/works` смотрит от лица одного хозяйства), поэтому экран сначала
 * выбирает хозяйство, и только потом читает его работы — `actor_id` уходит на
 * сервер фильтром внутри области вошедшего.
 */
export function farmScoped(name: string, farmId: string): string {
  return `${name}${FARM_SCOPE}${farmId}`;
}

/** Срез запроса одного хозяйства — тот же, по которому пишется ввод. */
export function farmParams(params: QueryParams, farmId: string): QueryParams {
  return { ...params, actor_id: Number(farmId) };
}

export function readEntryDataset(name: string, params: QueryParams): Promise<unknown> | null {
  const at = name.indexOf(FARM_SCOPE);
  if (at > 0) {
    const farmId = name.slice(at + FARM_SCOPE.length);
    return pick(name.slice(0, at), () => corpus(farmParams(params, farmId), 'works'));
  }
  // Без хозяйства — только оглавление: срез, циклы, культуры.
  return pick(name, () => corpus(params, 'index'));
}

function pick(name: string, read: () => Promise<CanonCorpus>): Promise<unknown> | null {
  if (name === FILES.meta) return read().then((data) => data.meta);
  if (name === FILES.crops) return read().then((data) => data.crops);
  if (name === FILES.cycles) return read().then((data) => data.cycles);
  if (name === FILES.farms) return read().then((data) => data.farms);
  if (name === FILES.contours) return read().then((data) => data.contours);

  const worksOf = suffix(name, 'works-');
  if (worksOf !== null) {
    if (worksOf === ALL_CYCLES) return read().then((data) => data.works);
    return read().then((data) => data.works.filter((row) => row.cycle_id === worksOf));
  }

  const obligationsOf = suffix(name, 'obligations-');
  if (obligationsOf !== null) {
    if (obligationsOf === ALL_CYCLES) return read().then((data) => data.obligations);
    return read().then((data) => {
      const mine = new Set(
        data.works.filter((row) => row.cycle_id === obligationsOf).map((row) => row.id),
      );
      return data.obligations.filter((row) => mine.has(row.work_id));
    });
  }

  return null;
}
